import type { SubscriptionFrequency } from "../SubscriptionFrequency.js";
import { subscriptionFrequencyToWeeksMap } from "./subscriptionFrequencyToWeeksMap.js";

// Shipping happens only on Monday–Thursday.
const shippingDays = [1, 2, 3, 4]; // getDay(): 1 = Mon ... 4 = Thu
const maxDifferenceInDays = 30;

export function isShippingDay(date: Date): boolean {
	return shippingDays.includes(date.getDay());
}

const nonShippingDays = Array.from({ length: 7 }, (_, day) => day).filter((day) => !shippingDays.includes(day));

/**
 * Returns the next shipping day (Mon–Thu) strictly after `from`:
 *  - Mon → Tue, Tue → Wed, Wed → Thu
 *  - Thu → Mon (Fri/Sat/Sun are skipped)
 *  - Fri/Sat/Sun → Mon
 */
function nextShippingDay(from: Date): Date {
	const date = new Date(from);

	do {
		date.setDate(date.getDate() + 1);
	} while (!isShippingDay(date));

	return date;
}

/**
 * The window of dispatch dates a customer may choose for a membership, given `today` and the
 * subscription `frequency`: the nearest is the next shipping day, the farthest is the next
 * shipping day after `min(30, weeks * 7)` days.
 */
export function calculateMembershipShippingDate(
	today: Date,
	frequency: SubscriptionFrequency,
): { nearest: Date; farthest: Date; excludedDays: number[] } {
	const nearest = nextShippingDay(today);

	const maxDifference = Math.min(maxDifferenceInDays, subscriptionFrequencyToWeeksMap[frequency] * 7);
	const latest = new Date(today);
	latest.setDate(latest.getDate() + maxDifference);
	const farthest = nextShippingDay(latest);

	return { nearest, farthest, excludedDays: nonShippingDays };
}
